<template>
  <div class="ignite-page">
    <NavIgnite />
    <header class="ignite-hero">
      <img src="../assets/Hanga-24/home/insta-4.jpg" alt="Rwanda's startup community at Hanga PitchFest" class="ignite-hero-image">
      <div class="ignite-container hero-content">
        <p class="eyebrow hero-eyebrow">HANGA VENTURE IGNITE</p>
        <h1>Big ideas.<br>Brighter futures.</h1>
        <p class="hero-description">Empowering Rwandan startups with early-stage funding to spark growth, drive innovation, and build Rwanda's digital future.</p>
        <div class="hero-actions">
          <a class="ignite-button" href="https://hanga.acceleratorapp.co/apply/program/hanga-venture-ignite" target="_blank" rel="noopener noreferrer">Apply now <span aria-hidden="true">↗</span></a>
          <button class="hero-secondary" type="button" @click="scrollToTiers">Explore the two tiers <span aria-hidden="true">↓</span></button>
        </div>
        <div class="hero-note"><span></span> Supporting startups from idea to market</div>
      </div>
    </header>
    <main>
      <section class="ignite-container intro-section" aria-labelledby="intro-title">
        <div>
          <p class="eyebrow">YOUR NEXT CHAPTER STARTS HERE</p>
          <h2 id="intro-title">An idea worth building.<br>A future worth funding.</h2>
        </div>
        <div class="intro-copy">
          <h3>At the ideation or MVP stage?</h3>
          <p>Hanga Venture Ignite provides essential financial support to early-stage Rwandan startups, helping them overcome initial capital constraints and kickstart their operations.</p>
          <p>By supporting innovation and entrepreneurship, the program helps startups attract further investment, accelerate their growth, and contribute to Rwanda's economic development.</p>
        </div>
        <div class="community-images">
          <img src="../assets/nsrh/n-srh-2.jpg" alt="Entrepreneurs connecting at a Hanga event" loading="lazy">
          <img src="../assets/nsrh/n-srh-1.jpg" alt="Hanga startup community sharing ideas" loading="lazy">
        </div>
      </section>
      <section id="funding-tiers" class="tiers-section" aria-labelledby="tiers-title">
        <div class="ignite-container">
          <div class="section-heading">
            <p class="eyebrow">TWO TIERS. ONE AMBITION.</p>
            <h2 id="tiers-title">Support for your stage of growth.</h2>
            <p>Grants for early-stage startups with high growth potential, from validating your first idea to taking your MVP to market.</p>
          </div>
          <article class="tier-card">
            <div class="tier-image-wrap">
              <img src="../assets/idea.jpeg" alt="Turning a startup idea into a business" loading="lazy">
              <span class="tier-stage">01 / IDEATION STAGE</span>
            </div>
            <div class="tier-content">
              <p class="eyebrow">BUILD YOUR FOUNDATION</p>
              <h3>Tier 1 <span>— Ignite</span></h3>
              <p class="tier-paragraph">
                    This tier is designed to support startups at the ideation stage, where the primary focus is on developing and validating initial ideas and concepts. The grants aim to help startups overcome initial capital constraints, enabling them to progress from a concept to a tangible business proposition. 
                    The expected outcomes for the startups in this grant tier are:
                  </p>
          
                  <p class="tier-paragraph">
                    <span class="font-bold text-darkGray">Proof of concept:</span> a validated prototype that effectively addresses a specific market need and demonstrates feasibility, 
                    an initial market validation indicating customer interest and demand for the product or service, and a clear value proposition that differentiates the startup from competitors.
                  </p>

                  <p class="tier-paragraph">
                    <span class="font-bold text-darkGray">Enhanced team capabilities:</span> strengthened team expertise and readiness to tackle subsequent stages of development,
                     and established mentorship and advisory relationships that provide ongoing support.
                  </p>

                  <p class="tier-paragraph">
                    <span class="font-bold text-darkGray">Early market engagement: </span> an increase in market awareness and interest in the startup's offerings, and
                     positive feedback from early adopters and potential customers that guides further development.
                  </p>
            </div>
          </article>
          <article class="tier-card tier-card-reverse">
            <div class="tier-image-wrap">
              <img src="../assets/Hanga-24/network/hero-section.jpg" alt="Startup founders networking with the Hanga community" loading="lazy">
              <span class="tier-stage">02 / MVP STAGE</span>
            </div>
            <div class="tier-content">
              <p class="eyebrow">TAKE THE NEXT STEP</p>
              <h3>Tier 2 <span>— Ignite+</span></h3>
              <p class="tier-paragraph">
                    This tier targets startups that have developed a Minimum Viable Product (MVP) and are seeking to refine, scale, and enter the market. The grants aim to support these startups in overcoming challenges associated with early market entry and expansion, providing the necessary resources to achieve significant growth.
                     The expected outcomes for the startups in this grant tier are:
                  </p>
          
                  <p class="tier-paragraph">
                    <span class="font-bold text-darkGray">Market readiness: </span> an enhanced MVP that is fully ready for market entry and capable of meeting customer needs and expectations, successful market launch with initial user acquisition and revenue generation, 
                    and a clear go-to-market strategy that positions the startup for rapid growth.
                  </p>

                  <p class="tier-paragraph">
                    <span class="font-bold text-darkGray">Business growth and expansion:</span>  an expanded team with the necessary skills and expertise to support ongoing development and operations, established partnerships and
                     pilot projects that validate the product and open avenues for further expansion, and a scalable business model that supports sustainable growth.
                  </p>

                  <p class="tier-paragraph">
                    <span class="font-bold text-darkGray">Increased investment attractiveness: </span> a well-developed product and initial market traction that increase the startup's attractiveness to potential investors,
                     secured intellectual property and regulatory compliance that mitigate risks and enhance investor confidence, and demonstrated potential for significant return on investment.</p>
            </div>
          </article>
          <div class="partner-strip">
            <p class="eyebrow">SUPPORTING RWANDA'S ENTREPRENEURS</p>
            <img src="../assets/brd.png" alt="Development Bank of Rwanda" loading="lazy">
          </div>
        </div>
      </section>
      <section class="ignite-container application-section" aria-labelledby="application-title">
        <div class="section-heading">
          <p class="eyebrow">PREPARE YOUR APPLICATION</p>
          <h2 id="application-title">Everything you need to get started.</h2>
          <p>Check the requirements and have your documents ready before applying.</p>
        </div>
        <div class="application-accordion">
          <div class="accordion-item">
            <h3><button type="button" :aria-expanded="isEligibilityOpen" aria-controls="ignite-documents" @click="toogleEligibility"><span>Required documents</span><svg class="accordion-icon" :class="{ 'is-open': isEligibilityOpen }" viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 24h33M25 9l15 15-15 15" /></svg></button></h3>
            <div v-if="isEligibilityOpen" id="ignite-documents" class="accordion-body">
              <h4>A. For initial assessment</h4>
              <ul><li v-for="(item, index) in eligibility" :key="index">{{ item.text }}</li></ul>
              <h4>B. Shortlisted candidates from the initial assessment</h4>
              <ul><li v-for="(item, index) in shortlisted" :key="index">{{ item.text }}</li></ul>
            </div>
          </div>
          <div class="accordion-item">
            <h3><button type="button" :aria-expanded="isCriteriaOpen" aria-controls="ignite-guidelines" @click="toogleCriteria"><span>Application guidelines</span><svg class="accordion-icon" :class="{ 'is-open': isCriteriaOpen }" viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 24h33M25 9l15 15-15 15" /></svg></button></h3>
            <div v-if="isCriteriaOpen" id="ignite-guidelines" class="accordion-body">
              <ul><li v-for="(item, index) in criteria" :key="index" v-html="item.criteri"></li></ul>
            </div>
          </div>
          <div class="accordion-item">
            <h3><button type="button" :aria-expanded="isLastOpen" aria-controls="ignite-eligibility" @click="toogleLast"><span>Eligibility criteria</span><svg class="accordion-icon" :class="{ 'is-open': isLastOpen }" viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 24h33M25 9l15 15-15 15" /></svg></button></h3>
            <div v-if="isLastOpen" id="ignite-eligibility" class="accordion-body">
              <p>Applications will be assessed based on the following criteria.</p>
              <ul><li v-for="(item, index) in lastSection" :key="index">{{ item.text }}</li></ul>
            </div>
          </div>
        </div>
        <div class="application-callout">
          <div><p class="eyebrow">TURN POSSIBILITY INTO PROGRESS</p><h3>Ready to ignite your venture?</h3><p>Your next chapter starts with an application.</p></div>
          <a class="ignite-button" href="https://hanga.acceleratorapp.co/apply/program/hanga-venture-ignite" target="_blank" rel="noopener noreferrer">Apply now <span aria-hidden="true">↗</span></a>
        </div>
        <p class="faq-note">Have a question? <router-link to="/ignite-faq">Explore our FAQs →</router-link></p>
      </section>
    </main>
    <scrollButton />
    <NewsLetter class="ignite-newsletter" />
    <FooterPage />
  </div>
</template>
<script>
import NavIgnite from '@/components/NavIgnite.vue';
import FooterPage from '@/components/footerPage.vue';
import NewsLetter from '@/components/NewsLetter.vue';
import scrollButton from '@/components/scrollButton.vue';
export default{
  name:"HangaIgnite",
  components:{
          NavIgnite,
          FooterPage,
          NewsLetter,
          scrollButton
      },

      data(){

        return{

          eligibility: [
        {
          text: 'Completed application',
        },
        {
          text: 'Pitch deck in the template provided',
        },
        {
          text: 'Certificate(s) of incorporation/registration,',
        },
        {
          text: 'Income and expenditure statements for the period of operation',
        }
       
      ],

      shortlisted:[
        {
          text: 'Bank statement of the start up for the last 12 months',
        },
        {
          text: 'Applicable permit, licenses & clearances such as Tax Clearances to ensure Fully compliancy of all expected legal, statutory, regulatory, and financial requirements.',
        },
      
        {
          text: 'Shareholding documentation, identification documentation for shareholders, directors and senior management; and documentation of the ultimate beneficial owners.',
        }


      ],

      criteria: [

        {
          criteri: 'All applications must be submitted online: <a href="https://hanga.acceleratorapp.co/apply/program/hanga-venture-ignite" target="_blank" rel="noopener noreferrer" class="underline">Apply to Hanga Venture Ignite</a>'
        },

        {
          criteri: 'If you still have questions, please contact: <a href="mailto:hanga.ventureignite@risa.gov.rw" class="underline">hanga.ventureignite@risa.gov.rw</a>'
        },
        {
          criteri: 'Applications must be submitted in English.'
        },
        {
          criteri: 'Applicants are required to fill all the mandatory fields in the application form. Applications with not all filled-in fields or with required documents not provided are not eligible.'
        },
       
        {
          criteri: 'Only one application per startup is allowed per call. In case of multiple submissions only one application will be considered'
        },

      ],

      lastSection:[

      {
          text: 'The entity will be compliant with the definition of a startup within the understanding of this facility',
        },
      {
          text: 'Legal entity status  Be a legal entity, duly registered in Rwanda (the startups will be required to submit their certificate of incorporation/registration), acting as the sole applicant, directly responsible for the preparation and management of the grant application i.e. the implementing entity should be the one applying for the grant.',
        },
      {
          text: 'Startups should have been in operation (from the time of an official registration) for a maximum of five years at the time of application.',
        },
      {
          text: 'The startup should not have received a grant previously within the same tier within this facility. Startups that have successfully completed a tier 1 grant implementation can apply for tier 2, to support startups that have validated their business models to obtain additional funding for scaling.',
        },
      {
          text: 'Only one application per startup is allowed within the context of a specific call of proposals / cut off dates. Startups submitting multiple applications for one call for proposal will be disqualified.',
        },
      {
          text: 'The startups will need to have real identified viability potential by showing evidence that they are filling gaps in one or more of four dimensions  technical performance, market opportunity, team skills and dedication, and a plan for financial investment and return.',
        },
      {
          text: 'The startups must have a clear innovation, through the use of digital technology, that it is testing/developing.',
        },
      {
          text: 'Startups will be required to provide information in the application form, information to demonstrate the current setup of their financial management system guided by the minimum financial management requirements',
        },
      {
          text: 'Startups that do not meet the minimum financial management requirements are encouraged to include financial management support as part of the activities to be funded their application. This support will help the startup develop essential financial processes, implement accounting systems, and ensure compliance with legal and regulatory requirements.',
        },
      {
          text: "Startups applying for the Tier 1 grants must meet the following requirements:- The startups should not have significant revenue generation. Total revenue should be less than USD 10,000 for the period of operation. This should be supported by annual income and expenditure statements for the period of operation.- Startups should be at ideation stage with a clearly defined innovative concept. The concept should demonstrate significant potential for high growth. No fully developed product is required at this stage.",
        },
        {
          text: "Startups applying for the Tier 2 grants must meet the following criteria: Startups should have initial revenue generation but not exceeding USD 50,000 annual revenue for the period of operation. This should be supported by annual income and expenditure statements for the period of operation.- Startups have developed an MVP that includes essential features to meet early adopters' needs. The MVP should show clear potential for significant growth and scalability."
        },

      ],


     heroText: [
    "Empowering Rwandan Startups ",
    "with Early-Stage Funding"
      
    ],

    baseText:[
        'Hanga Venture Ignite fuels early-stage Rwandan startups ',
        'with funding to spark growth, drive innovation,',
        " and boost Rwanda's digital future.",

    ],

    isEligibilityOpen: false,
    isCriteriaOpen: false,
    isLastOpen:false,



    statsItems: [
      {  finalValue: 200, currentValue: 0, suffix: '+', description: 'Startups'},
      {  finalValue: 20, currentValue: 0, suffix: '+', description: 'Hanga SRH' },
      { finalValue: 190, currentValue: 0, suffix: '+', description: 'Hanga Hubs' },
    ],
    startX: 0,

        }

      },

      methods: {
  scrollToTiers() { document.getElementById('funding-tiers').scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }); },
  scrollLeft() {
    this.$refs.videoWrapper.scrollBy({ left: -500, behavior: 'smooth' });
  },
  scrollRight() {
    this.$refs.videoWrapper.scrollBy({ left: 500, behavior: 'smooth' });
  },

  toogleEligibility() {
      this.isEligibilityOpen = !this.isEligibilityOpen

    },

    toogleCriteria() {
      this.isCriteriaOpen = !this.isCriteriaOpen
    },

    toogleLast(){

      this.isLastOpen =!this.isLastOpen

    }

},


}

</script>
<style scoped>
.ignite-page { color: #030c37; background: #fff; font-family: Gramatika, sans-serif; }
.ignite-container { width: min(1200px, calc(100% - 96px)); margin-inline: auto; }
.ignite-hero { position: relative; isolation: isolate; padding-top: 96px; background: #030c37; color: #fff; }
.ignite-hero-image { position: absolute; z-index: -2; inset: 0; width: 100%; height: 100%; object-fit: cover; object-position: center 40%; }
.ignite-hero::after { content: ''; position: absolute; inset: 0; z-index: -1; background: rgba(0,0,0,.6); }
.hero-content { padding-block: clamp(64px, 8vw, 112px) 64px; }
.eyebrow { font-size: 12px; font-weight: 700; letter-spacing: .16em; line-height: 1.6; color: #00a8a8; margin-bottom: 18px; }
.hero-eyebrow { color: #d4fff5; }
.ignite-page h1 { font-family: Rockinsoda, serif; font-size: clamp(48px, 6.4vw, 88px); line-height: 1.12; letter-spacing: normal; max-width: 900px; margin: 0 0 26px; font-weight: 400; }
.hero-description { max-width: 600px; font-size: clamp(17px, 1.7vw, 21px); line-height: 1.7; color: #fff; }
.hero-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 24px 32px; margin-top: 32px; }
.ignite-button { display: inline-flex; align-items: center; justify-content: space-between; gap: 32px; min-height: 54px; padding: 15px 26px; border-radius: 6px; background: #00a8a8; color: white; font-size: 16px; font-weight: 700; transition: background .2s, transform .2s; }
.ignite-button:hover { background: #243163; transform: translateY(-2px); }
.hero-secondary { display: inline-flex; align-items: center; gap: 16px; min-height: 44px; font-size: 15px; }
.hero-secondary:hover { color: #d4fff5; }
.hero-note { display: flex; align-items: center; gap: 10px; margin-top: 48px; font-size: 12px; color: #fff; }
.hero-note span { width: 7px; height: 7px; border-radius: 50%; background: #00a8a8; flex-shrink: 0; }
.intro-section { display: grid; grid-template-columns: 1fr 1fr; gap: 48px 64px; padding-block: 88px; }
.ignite-page h2 { font-family: Gramatika, sans-serif; font-size: clamp(30px, 3.3vw, 46px); line-height: 1.2; letter-spacing: normal; font-weight: 700; }
.intro-copy h3 { font-size: 21px; font-weight: 700; margin-bottom: 16px; }
.intro-copy p, .section-heading > p:last-child { font-size: 16px; line-height: 1.8; color: #243163; }
.intro-copy p + p { margin-top: 16px; }
.community-images { grid-column: 1 / -1; display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.community-images img { width: 100%; aspect-ratio: 16 / 10; object-fit: cover; border-radius: 10px; }
.tiers-section { background: #fff; padding-block: 88px 56px; scroll-margin-top: 120px; }
.section-heading { max-width: 700px; margin: 0 auto 40px; text-align: center; }
.section-heading h2 { margin-bottom: 18px; }
.tier-card { display: grid; grid-template-columns: .85fr 1.15fr; background: white; border: 1px solid #d4fff5; border-radius: 14px; overflow: hidden; margin-top: 32px; }
.tier-image-wrap { position: relative; min-height: 320px; }
.tier-image-wrap img { width: 100%; height: 100%; position: absolute; object-fit: cover; }
.tier-stage { position: absolute; left: 24px; top: 24px; background: #fff; padding: 10px 14px; border-radius: 4px; color: #243163; font-size: 11px; font-weight: 700; letter-spacing: .08em; }
.tier-content { padding: clamp(24px, 3.5vw, 48px); min-width: 0; }
.tier-content .eyebrow { margin-bottom: 10px; }
.tier-content h3 { font-size: clamp(26px, 3vw, 36px); letter-spacing: normal; font-weight: 700; margin-bottom: 22px; }
.tier-content h3 span { color: #00a8a8; }
.tier-paragraph { color: #243163; font-size: 15px; line-height: 1.8; margin-top: 18px; }
.tier-paragraph span { display: block; color: #243163; font-weight: 700; margin-bottom: 3px; }
.tier-card-reverse { grid-template-columns: 1.15fr .85fr; }
.tier-card-reverse .tier-image-wrap { order: 2; }
.partner-strip { display: flex; flex-direction: column; align-items: center; gap: 8px; padding-top: 56px; text-align: center; }
.partner-strip img { max-width: 220px; width: 100%; height: 72px; object-fit: contain; }
.partner-strip .eyebrow { font-size: 10px; color: #243163; }
.application-section { padding-block: 88px 64px; }
.application-accordion { display: grid; gap: clamp(20px, 3vw, 40px); }
.accordion-item { background: #d4fff5; color: #243163; }
.accordion-item h3 { margin: 0; }
.accordion-item button { display: flex; align-items: center; justify-content: space-between; gap: 20px; width: 100%; min-height: 120px; padding: 32px; text-align: left; font-family: Rockinsoda, serif; font-size: clamp(24px, 3.5vw, 48px); line-height: 1.2; font-weight: 400; text-transform: uppercase; }
.accordion-item button:hover { background: #d4fff5; }
.accordion-icon { flex-shrink: 0; height: 48px; width: 48px; color: #243163; transition: transform .2s ease; }
.accordion-icon.is-open { transform: rotate(90deg); }
.accordion-body { padding: 0 32px 32px; color: #243163; font-size: 16px; line-height: 1.8; overflow-wrap: anywhere; }
.accordion-body h4 { color: #030c37; font-weight: 700; margin-block: 16px; font-size: 18px; }
.accordion-body li { padding-left: 8px; margin: 12px 0 12px 18px; list-style-type: disc; }
.application-callout { display: flex; align-items: center; justify-content: space-between; gap: 32px; padding: 40px; margin-top: 56px; border-radius: 12px; }
.application-callout h3 { font-size: clamp(26px, 3vw, 36px); font-weight: 700; letter-spacing: normal; line-height: 1.2; margin-bottom: 12px; }
.application-callout .eyebrow { font-size: 10px; margin-bottom: 14px; }
.application-callout .ignite-button { flex-shrink: 0; }
.application-callout p:last-child { color: #243163; line-height: 1.6; }
.faq-note { text-align: center; color: #243163; margin-top: 28px; font-size: 15px; }
.faq-note a { color: #00a8a8; font-weight: 700; text-decoration: underline; text-underline-offset: 4px; }
.ignite-page :is(a, button):focus-visible { outline: 3px solid #f7810b; outline-offset: 5px; }
.ignite-newsletter :deep(.mt-32) { margin-top: 0; }
.ignite-newsletter :deep(input) { min-width: 0; width: 100%; }
@media (max-width: 900px) {
  .ignite-container { width: calc(100% - 48px); }
  .intro-section { gap: 32px; padding-block: 64px; }
  .tier-card, .tier-card-reverse { grid-template-columns: 1fr; }
  .tier-image-wrap { min-height: 0; aspect-ratio: 16 / 8; }
  .tier-card-reverse .tier-image-wrap { order: 0; }
  .tier-content { padding: 32px; }
  .tiers-section, .application-section { padding-block: 64px; }
  .application-callout { flex-direction: column; align-items: flex-start; }
}
@media (max-width: 600px) {
  .ignite-container { width: calc(100% - 40px); }
  .hero-content { padding-block: 56px 40px; }
  .ignite-page h1 { font-size: clamp(42px, 11vw, 62px); }
  .hero-description { font-size: 16px; }
  .hero-actions { align-items: stretch; flex-direction: column; gap: 14px; }
  .hero-secondary { justify-content: center; }
  .hero-note { margin-top: 30px; font-size: 11px; }
  .intro-section { grid-template-columns: 1fr; padding-block: 48px; }
  .community-images { gap: 12px; }
  .community-images img { aspect-ratio: 3 / 4; }
  .eyebrow { font-size: 10px; }
  .section-heading { text-align: left; margin-bottom: 28px; }
  .tier-card { margin-top: 22px; }
  .tier-content { padding: 24px 20px; }
  .tier-stage { top: 16px; left: 16px; font-size: 10px; }
  .tiers-section, .application-section { padding-block: 48px; }
  .accordion-item button { min-height: 100px; padding: 24px 20px; gap: 16px; }
  .accordion-icon { width: 32px; height: 32px; }
  .accordion-body { padding: 0 20px 24px; font-size: 15px; }
  .application-callout { padding: 28px 22px; margin-top: 36px; }
  .application-callout .ignite-button { width: 100%; }
  .faq-note { line-height: 1.8; }
  .ignite-newsletter :deep(.px-8) { padding-inline: 20px; }
}
@media (max-width: 640px) { .ignite-hero { padding-top: 76px; } }
@media (prefers-reduced-motion: reduce) { .ignite-button, .accordion-icon { transition: none; } }
</style>
